import React, { Fragment, useState } from "react";
import { DrawerPageProps } from "../types";
import { Bundle } from "../../types";
import { BundleProvider } from "../../BundleContext";
import { BundleDrawerFooter } from "../BundleDrawerFooter";
import { CoverImage } from "@/components/CoverImage";
import { BundleDetails } from "../../BundleDetails";
import { BundleIncentive } from "../../BundleIncentive";
import { BundleProfileSelector } from "../../BundleProfileSelector";
import { BundleTitle } from "../../BundleTitle";
import { BundleProductList } from "../../BundleProductList";
import { BuyButton } from "@/features/cart";
import {
  SubscriptionSelector,
  SubscriptionSelectorValue,
} from "@/features/product/SubscriptionSelector";
import { DroppsProduct, productSelect, variantSelect } from "@/store";
import { storefrontApi } from "@/services/storefront";
import { BundlePrice } from "../../BundlePrice";

type ConfirmBundlePageProps = DrawerPageProps & {
  bundle: Bundle;
};

export const ConfirmBundlePage = ({
  goToFirstPage,
  bundle,
}: ConfirmBundlePageProps) => {
  const profileNames = bundle.profiles.map((profile) => profile.name);
  const [selectedProfile, setSelectedProfile] = useState(bundle.profiles[0]);
  const [subToggle, setSubToggle] =
    useState<SubscriptionSelectorValue>("subscription");

  const data = storefrontApi.useHandles(
    selectedProfile.products,
    `${bundle.title}-${selectedProfile.name}`
  );
  const products = data.data?.all;

  return (
    <BundleProvider bundle={bundle}>
      <div>
        <CoverImage
          src={bundle.image.src}
          width={bundle.image.width}
          height={bundle.image.height}
          loading={bundle.image.loading}
          alt={bundle.image.alt}
        />
      </div>
      {bundle.banner && (
        <div className="bg-secondary-500 px-4 py-2 text-white">
          <h3 className="subtitle text-center font-semibold text-inherit">
            {bundle.banner}
          </h3>
        </div>
      )}
      <div className="grow space-y-6 py-5">
        <div className="space-y-6 px-5">
          <div className="space-y-2">
            <BundleTitle />
            <BundleDetails />
            <BundleIncentive />
          </div>
          <BundleProfileSelector
            profiles={profileNames}
            onChange={(value) => {
              setSelectedProfile(
                bundle.profiles?.find((profile) => value === profile.name) ??
                  selectedProfile
              );
            }}
            value={selectedProfile.name}
          />
        </div>

        {products && <BundleProductList className="px-1" products={products} />}
        {!products && (
          <div className="space-y-4 px-5">
            <div className="h-6 w-full bg-gray-100"></div>
            {bundle.profiles[0].products.map((p, i) => {
              return (
                <Fragment key={i}>
                  <hr className="divider-x my-0" />
                  <div className="h-20 w-full bg-gray-100"></div>
                </Fragment>
              );
            })}
          </div>
        )}
        <div className="space-y-4 px-5">
          <BundlePrice
            isSubscription={subToggle === "subscription"}
            products={products ?? []}
          />
          <SubscriptionSelector
            value={subToggle}
            onChange={(value) => {
              setSubToggle(value);
            }}
            size="sm"
            cta={
              <p className="body text-center text-xs">
                <span className="whitespace-nowrap">
                  Tax calculated in checkout.
                </span>{" "}
                <span className="whitespace-nowrap">
                  You saved an additional 25% by subscribing.
                </span>{" "}
                <span className="whitespace-nowrap">
                  Change or cancel anytime.
                </span>{" "}
                <span className="whitespace-nowrap">Ships every 4 months.</span>
              </p>
            }
          />
        </div>
      </div>
      <BundleDrawerFooter>
        {products && (
          <BuyButton
            className="w-full"
            size="lg"
            data-id={
              subToggle === "subscription"
                ? "checkout-bundle-drawer-subscription"
                : "checkout-bundle-drawer-spot"
            }
            discountCode={bundle.discountCode}
            sourceModule={"bundle_drawer"}
            items={products.map((product: DroppsProduct) => {
              const variant = productSelect.defaultVariant(product);
              return variantSelect.cartAddLineItem(variant, {
                subscription:
                  subToggle === "subscription"
                    ? productSelect.defaultSellingPlan(product)
                    : false,
              });
            })}
          >
            Checkout
          </BuyButton>
        )}
      </BundleDrawerFooter>
    </BundleProvider>
  );
};
